import {faEnvelope} from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import {faLocationDot} from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import {faPhone} from "@fortawesome/pro-solid-svg-icons/faPhone";
import {faTrash} from "@fortawesome/pro-solid-svg-icons/faTrash";
import {faUser} from "@fortawesome/pro-solid-svg-icons/faUser";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {AddressType} from "@twigsby/client-data";
import {
    Button,
    ColourOutline,
    ColourSolid,
    CountryCodeControl,
    EmailControl,
    FlexAlign,
    FlexGap,
    FlexJustify,
    FormWrapperLabel,
    ItemRow,
    ItemRowSize,
    Pill,
    PillSize,
    StackHorizontal,
    StackVertical,
    useLayout,
    ValidationPatterns
} from "@twigsby/frontend-framework";
import {Controller, useFormContext} from "@twigsby/frontend-framework/lib/ReactHookForm";
import React, {useEffect, useState} from "react";
import {ValidTelephone} from "../../ValidTelephone";

interface AddressImportRow extends AddressType {
    id: string;
    validRow: boolean;
    fieldIndex: number;
    removeCallback: any;
    lookUpsActive: (value: boolean) => void;
    embedded?: boolean;
}

const AddressImporterRow = (props: AddressImportRow) => {
    const { isMedium, isLarge, isXL } = useLayout();
    const {
        control,
        getValues,
        trigger,
        register,
        setError,
        clearErrors,
        formState
    } = useFormContext();

    const [ editing, setEditing ] = useState<boolean>(!props.validRow);

    const HandleRemove = (id: number) => {
        props.removeCallback(id);
    }

    useEffect(() => {
        trigger().then((r) => console.log('field', r));
    }, []);

    if(!editing) {
        return (
            <ItemRow
                static={true}
                size={ItemRowSize.Large}
                extraProps={{
                    'data-row-id': props.fieldIndex
                }}
            >
                <div style={{ display: 'grid', gap: 'var(--sm-unit)', gridTemplateColumns: isXL ? 'repeat(4, 1fr)' : isMedium ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)', alignItems: 'center' }}>
                    <div><FontAwesomeIcon icon={faUser} fixedWidth={true} /> {getValues()['Addresses'][props.fieldIndex].name}</div>
                    <div><FontAwesomeIcon icon={faPhone} fixedWidth={true} /> {getValues()['Addresses'][props.fieldIndex].phoneNo}</div>
                    <div><FontAwesomeIcon icon={faEnvelope} fixedWidth={true} /> {getValues()['Addresses'][props.fieldIndex].emailAddress}</div>
                    <StackHorizontal align={FlexAlign.Center} gap={FlexGap.ExtraSmall}>
                        <FontAwesomeIcon icon={faLocationDot} fixedWidth={true} />
                        <div>
                            {getValues()['Addresses'][props.fieldIndex].businessName ? getValues()['Addresses'][props.fieldIndex].businessName + ', ' : ''}
                            {getValues()['Addresses'][props.fieldIndex].propertyName}, {getValues()['Addresses'][props.fieldIndex].addressLine1}, {getValues()['Addresses'][props.fieldIndex].addressLine2}, {getValues()['Addresses'][props.fieldIndex].addressLine3}, {getValues()['Addresses'][props.fieldIndex].postCode}, {getValues()['Addresses'][props.fieldIndex].countryCode}
                        </div>
                    </StackHorizontal>
                </div>

                {!isLarge && (
                    <StackVertical gap={FlexGap.Small} align={FlexAlign.Start} justify={FlexJustify.Center}>
                        <Pill content={'Valid'} colour={ColourSolid.Success} size={PillSize.Small} />
                        <Button title={'Remove'} colour={ColourSolid.Alert} onClick={() => HandleRemove(props.fieldIndex)}  icon={faTrash} iconOnly={true} fullWidth={!isLarge} squared={true} />
                    </StackVertical>
                )}

                {isLarge && (
                    <StackHorizontal gap={FlexGap.Small} justify={FlexJustify.End} align={FlexAlign.Center} extraClass={isLarge ? '' : 'mt-sm'}>
                        <Pill content={'Valid'} colour={ColourSolid.Success} size={PillSize.Small} />
                        <Button title={'Remove'} colour={ColourSolid.Alert} onClick={() => HandleRemove(props.fieldIndex)}  icon={faTrash} fullWidth={!isLarge} squared={true} />
                    </StackHorizontal>
                )}
            </ItemRow>
        )
    }

    return (
        <div className={`address-row-requires-attention ${!props.embedded ? 'm-sm' : ''}`} data-row-id={props.fieldIndex}>
            <ItemRow
                static={true}
                size={ItemRowSize.Large}
                extraProps={{
                    'data-row-id': props.fieldIndex
                }}
            >
                <div style={{ display: 'grid', gap: 'var(--sm-unit)', gridTemplateRows: 'auto', gridTemplateColumns: isXL ? 'repeat(5, 1fr)' : isMedium ? 'repeat(3, 1fr)' : 'repeat(1, 1fr)'  }}>
                    <FormWrapperLabel
                        label={'Name'}
                        invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.name && formState.errors.Addresses[props.fieldIndex]?.name?.message !== ''}
                        validationMessage={formState.errors.Addresses ? formState.errors.Addresses[props.fieldIndex]?.name?.message as string : ''}
                    >
                        <input
                            type='text'
                            aria-invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.name ? 'true' : 'false'}
                            defaultValue={props.name}
                            {...register(`Addresses.${props.fieldIndex}.name`, { required: "This is a required field" })}
                        />
                    </FormWrapperLabel>

                    <FormWrapperLabel
                        label={'Business Name'}
                        invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.businessName && formState.errors.Addresses[props.fieldIndex]?.businessName?.message !== ''}
                        validationMessage={formState.errors.Addresses ? formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.businessName?.message as string : ''}
                    >
                        <input
                            type='text'
                            aria-invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.businessName ? 'true' : 'false'}
                            defaultValue={props.businessName}
                                {...register(`Addresses.${props.fieldIndex}.businessName`)} />
                    </FormWrapperLabel>

                    <FormWrapperLabel
                        label={'Property Name/Number'}
                        invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.propertyName && formState.errors.Addresses[props.fieldIndex]?.propertyName?.message !== ''}
                        validationMessage={formState.errors.Addresses ? formState.errors.Addresses[props.fieldIndex]?.propertyName?.message as string : ''}
                    >
                        <input
                            type='text'
                            aria-invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.propertyName ? 'true' : 'false'}
                            defaultValue={props.propertyName}
                            {...register(`Addresses.${props.fieldIndex}.propertyName`, { required: "This is a required field" })} />
                    </FormWrapperLabel>

                    <FormWrapperLabel
                        label={'Street'}
                        invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine1 && formState.errors.Addresses[props.fieldIndex]?.addressLine1?.message !== ''}
                        validationMessage={formState.errors.Addresses ? formState.errors.Addresses[props.fieldIndex]?.addressLine1?.message as string : ''}
                    >
                        <input
                            type='text'
                            aria-invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine1 ? 'true' : 'false'}
                            defaultValue={props.addressLine1}
                            {...register(`Addresses.${props.fieldIndex}.addressLine1`, { required: "This is a required field" })} />
                    </FormWrapperLabel>

                    <FormWrapperLabel
                        label={'City'}
                        invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine2 && formState.errors.Addresses[props.fieldIndex]?.addressLine2?.message !== ''}
                        validationMessage={formState.errors.Addresses ? formState.errors.Addresses[props.fieldIndex]?.addressLine2?.message as string : ''}
                    >
                        <input
                            type='text'
                            aria-invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine2 ? 'true' : 'false'}
                            defaultValue={props.addressLine2}
                            {...register(`Addresses.${props.fieldIndex}.addressLine2`, { required: "This is a required field" })} />
                    </FormWrapperLabel>

                    <FormWrapperLabel
                        label={'Locality'}
                        invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine3 && formState.errors.Addresses[props.fieldIndex]?.addressLine3?.message !== ''}
                        validationMessage={formState.errors.Addresses ? formState.errors.Addresses[props.fieldIndex]?.addressLine3?.message as string : ''}
                    >
                        <input
                            type='text'
                            aria-invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.addressLine3 ? 'true' : 'false'}
                            defaultValue={props.addressLine3}
                            {...register(`Addresses.${props.fieldIndex}.addressLine3`, { required: "This is a required field" })} />
                    </FormWrapperLabel>

                    <FormWrapperLabel
                        label={'Post Code'}
                        invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.postCode && formState.errors.Addresses[props.fieldIndex]?.postCode?.message !== ''}
                        validationMessage={formState.errors.Addresses ? formState.errors.Addresses[props.fieldIndex]?.postCode?.message as string : ''}
                    >
                        <input
                            type='text'
                            aria-invalid={formState.errors.Addresses && formState.errors.Addresses[props.fieldIndex]?.postCode ? 'true' : 'false'}
                            defaultValue={props.postCode}
                            {...register(`Addresses.${props.fieldIndex}.postCode`, { required: "This is a required field" })} />
                    </FormWrapperLabel>

                    <Controller
                        control={control}
                        name={`Addresses.${props.fieldIndex}.countryCode`}
                        defaultValue={props.countryCode}
                        rules={{ required: "Country Code is required" }}
                        render={({ field, fieldState }) =>
                            <FormWrapperLabel
                                label={'Country Code'}
                                invalid={fieldState.invalid}
                                validationMessage={fieldState.error?.message}
                            >
                                <CountryCodeControl
                                    name={'Country Code'}
                                    placeholder={'Country Code'}
                                    forwardValueCallback={(val) => field.onChange(val)}
                                    defaultValues={[props.countryCode]}
                                    singleSelection={true}
                                 />
                            </FormWrapperLabel>
                        }
                    />

                    <Controller
                        control={control}
                        name={`Addresses.${props.fieldIndex}.phoneNo`}
                        defaultValue={props.phoneNo}
                        rules={{ required: "Telephone is required", pattern: { value: ValidationPatterns.telephone, message: 'Telephone format is invalid' } }}
                        render={({ field, fieldState }) =>
                            <ValidTelephone
                                fieldState={fieldState}
                                placeholder={'Telephone Number'}
                                name={`Addresses.${props.fieldIndex}.phoneNo`}
                                defaultValue={props.phoneNo}
                                changeCallback={(e) => field.onChange(e)}
                                validationCallback={(message, isValid) => isValid ? clearErrors(`Addresses.${props.fieldIndex}.phoneNo`) : setError(`Addresses.${props.fieldIndex}.phoneNo`, { type: 'custom', message: message })}
                                lookupActiveCallback={(state) => props.lookUpsActive(state)}
                            />
                        }
                    />

                    <Controller
                        control={control}
                        name={`Addresses.${props.fieldIndex}.emailAddress`}
                        defaultValue={props.emailAddress}
                        rules={{ required: "E-mail is required" }}
                        render={({ field, fieldState }) =>
                            <FormWrapperLabel
                                label={'E-mail'}
                                invalid={fieldState.invalid}
                                validationMessage={fieldState.error?.message}
                            >
                                <EmailControl
                                    name={`Addresses.${props.fieldIndex}.emailAddress`}
                                    placeholder={'E-mail'}
                                    defaultValue={props.emailAddress}
                                    onChangeCallback={(val) => field.onChange(val)}
                                />
                            </FormWrapperLabel>
                        }
                    />
                </div>

                <StackHorizontal gap={FlexGap.Small} justify={FlexJustify.End} extraClass={isLarge ? '' : 'mt-lg'}>
                    <Button title={'Remove'} colour={ColourOutline.Alert} onClick={() => HandleRemove(props.fieldIndex)} icon={faTrash} iconOnly={isLarge} fullWidth={!isLarge} squared={true} />
               {/*     <Button title={'Edit'} disabled={!props.validRow} fullWidth={true} onClick={() => setEditing(!editing)} icon={<FontAwesomeIcon icon={faPencil} />} iconOnly={true} squared={true} />
               */}
                </StackHorizontal>

            </ItemRow>
        </div>
    )
}

export default AddressImporterRow;
